import React, { createContext, useContext, useState, useCallback } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
    id: string;
    type: ToastType;
    message: string;
    duration?: number;
}

interface ToastContextType {
    toasts: Toast[];
    showToast: (type: ToastType, message: string, duration?: number) => void;
    removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const showToast = useCallback((type: ToastType, message: string, duration = 4000) => {
        const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const newToast: Toast = { id, type, message, duration };

        setToasts(prev => [...prev, newToast]);

        if (duration > 0) {
            setTimeout(() => {
                removeToast(id);
            }, duration);
        }
    }, []);

    const removeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ toasts, showToast, removeToast }}>
            {children}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </ToastContext.Provider>
    );
};

// Toast Container Component
const ToastContainer: React.FC<{ toasts: Toast[]; removeToast: (id: string) => void }> = ({ toasts, removeToast }) => {
    if (toasts.length === 0) return null;

    return (
        <div className="fixed bottom-6 right-6 z-[200] flex flex-col gap-3 max-w-sm">
            {toasts.map(toast => (
                <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
            ))}
        </div>
    );
};

// Individual Toast Item
const ToastItem: React.FC<{ toast: Toast; onClose: () => void }> = ({ toast, onClose }) => {
    const getStyles = () => {
        switch (toast.type) {
            case 'success':
                return {
                    bg: 'bg-gradient-to-r from-green-500 to-emerald-500',
                    icon: 'fa-check-circle',
                    shadow: 'shadow-lg shadow-green-500/30'
                };
            case 'error':
                return {
                    bg: 'bg-gradient-to-r from-red-500 to-rose-500',
                    icon: 'fa-exclamation-circle',
                    shadow: 'shadow-lg shadow-red-500/30'
                };
            case 'warning':
                return {
                    bg: 'bg-gradient-to-r from-yellow-500 to-orange-500',
                    icon: 'fa-exclamation-triangle',
                    shadow: 'shadow-lg shadow-yellow-500/30'
                };
            case 'info':
            default:
                return {
                    bg: 'bg-gradient-to-r from-blue-500 to-indigo-500',
                    icon: 'fa-info-circle',
                    shadow: 'shadow-lg shadow-blue-500/30'
                };
        }
    };

    const styles = getStyles();

    return (
        <div
            className={`${styles.bg} ${styles.shadow} text-white px-5 py-4 rounded-2xl flex items-center gap-4 animate-slide-up backdrop-blur-sm min-w-[280px]`}
        >
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <i className={`fas ${styles.icon} text-lg`}></i>
            </div>
            <p className="flex-1 text-sm font-medium leading-snug">{toast.message}</p>
            <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
                <i className="fas fa-times text-sm"></i>
            </button>
        </div>
    );
};

export default ToastProvider;
